//
// Class store
//
// Stores and manages all classes that can be administered by the
// authenticated user.
//

import * as client from 'api/client';
import { LOGOUT, getToken } from './auth';
import { RECEIVE_ENTITIES } from './user';
import { LESSON_CREATED, LESSON_DELETED } from './lessons';
import { TEACHER_CREATED, TEACHER_DELETED } from './teachers';
import { showSuccess, showError } from './messages';

// ------------------------------------
// Action definitions
// ------------------------------------

export const SELECT_CLASS  = 'CLASSES/SELECT';
export const CREATE_CLASS  = 'CLASSES/CREATE';
export const CLASS_CREATED = 'CLASSES/CREATE_OK';
export const UPDATE_CLASS  = 'CLASSES/UPDATE';
export const CLASS_UPDATED = 'CLASSES/UPDATE_OK';
export const DELETE_CLASS  = 'CLASSES/DELETE';
export const CLASS_DELETED = 'CLASSES/DELETE_OK';
export const ERROR         = 'CLASSES/ERROR';

// ------------------------------------
// Actions creators
// ------------------------------------

export const selectClass = id => ({
  type: SELECT_CLASS,
  id
});

export const createClass = (name, schoolId) => (dispatch, getState) => {
  dispatch({ type: CREATE_CLASS });

  const state = getState();
  const token = getToken(state);

  const data = {
    name,
    school_id: schoolId
  };

  return client.post(`/api/classes`, data, token)
    .then(cls => {
      dispatch({ type: CLASS_CREATED, cls });
      dispatch(showSuccess('class.create.success'));
    })
    .catch(error => {
      dispatch(fail());
      dispatch(showError('class.create.failed'));
    });
};

export const deleteClass = id => (dispatch, getState) => {
  dispatch({ type: DELETE_CLASS });

  const state = getState();
  const token = getToken(state);

  return client.del(`/api/classes/${id}`, token)
    .then(() => {
      dispatch({ type: CLASS_DELETED, id });
      dispatch(showSuccess('class.delete.success'));
    })
    .catch(error => {
      dispatch(fail());
      dispatch(showError('class.delete.failed'));
    });
};

export const fail = () => ({ type: ERROR });

// ------------------------------------
// Initial state
// ------------------------------------

const initialState = {
  items: {},
  selected: null,
  pending: false
};

// ------------------------------------
// Reducer
// ------------------------------------

export default (state = initialState, action) => {
  switch (action.type) {

    // Populate store with received user data.
    case RECEIVE_ENTITIES: {
      const { users, classes } = action.entities;
      const user = Object.values(users)[0];
      const items = classes || {};
      const selected = user.classes[0] || null;

      return { ...state, items, selected };
    }

    // Set selected class.
    case SELECT_CLASS: {
      return { ...state, selected: action.id };
    }

    // CRUD request is pending.
    case CREATE_CLASS:
    case UPDATE_CLASS:
    case DELETE_CLASS: {
      return { ...state, pending: true };
    }

    // Update store with new/updated class.
    case CLASS_CREATED:
    case CLASS_UPDATED: {
      const { cls } = action;
      const items = { ...state.items, [`${cls.id}`]: cls };

      return { ...state, items, pending: false };
    }

    // Remove deleted class from the store.
    case CLASS_DELETED: {
      const items = { ...state.items };
      delete items[action.id];

      return { ...state, items, pending: false };
    }

    // Add lesson to its parent class.
    case LESSON_CREATED: {
      const { lesson } = action;
      const items = { ...state.items };
      const cls = items[lesson.class_id];

      items[cls.id] = Object.assign({}, cls, {
        lessons: [...cls.lessons, lesson.id]
      });

      return { ...state, items };
    }

    // Delete lesson from class to which it belonged.
    case LESSON_DELETED: {
      const items = { ...state.items };
      const cls = Object.values(items).find(cls =>
        cls.lessons.includes(action.id)
      );

      items[cls.id] = Object.assign({}, cls, {
        lessons: cls.lessons.filter(id => id !== action.id)
      });

      return { ...state, items };
    }

    // Add teacher to its parent class.
    case TEACHER_CREATED: {
      const { user, classId } = action;
      const items = { ...state.items };
      const item = items[classId];

      items[classId] = Object.assign({}, item, {
        teachers: [...item.teachers, user.id]
      });

      return { ...state, items };
    }

    // Delete teacher from class to which it belonged.
    case TEACHER_DELETED: {
      const items = { ...state.items };
      const item = items[action.classId];

      items[item.id] = Object.assign({}, item, {
        teachers: item.teachers.filter(id => id !== action.teacherId)
      });

      return { ...state, items };
    }

    // API request failed.
    case ERROR:
      return { ...state, pending: false };

    // Reset store on logout.
    case LOGOUT:
      return initialState;

    default:
      return state;
  }
};

// ------------------------------------
// Utils
// ------------------------------------

export const getAllClasses = (state) => {
  return Object.values(state.classes.items);
};

export const getClasses = (state, ids) => {
  const { items } = state.classes;
  return ids.map(id => ({ ...items[id] }));
};

export const getClass = (state, id) => {
  const { items } = state.classes;
  return items.hasOwnProperty(id)
    ? { ...items[id] }
    : null;
};

export const getSelectedClass = state => {
  const { items, selected } = state.classes;
  return selected
    ? { ...items[selected] }
    : null;
};



// WEBPACK FOOTER //
// ./src/store/modules/classes.js